<template>
    <section class="service-options" id="serviceOptions">
        <div class="service-options__container container">
            <StepBar />

            <StepLink v-if="!shortOnlineServiceBooking" :step="3" />

            <h3 class="title">Service Options</h3>

            <LoadingSpinner v-if="!finishedCollectingPricing" />

            <div class="service-options__desktop-columns" v-else>
                <div class="card service-options__card">
                    <div class="card__inner">
                        <div v-if="showNoOutstandingRecalls" class="service-options__notice" role="status">
                            <h3 class="service-options__notice-heading">No outstanding recalls found</h3>
                            <p class="service-options__notice-content">We've checked your vehicle registration and can confirm that there are currently no outstanding safety recalls showing for your vehicle. If you would like to book a service or MOT, please use the button below.</p>
                            <button type="button" class="btn btn--primary" @click="bookServiceOrMot">Book a service or MOT</button>
                        </div>
                        <ManufacturerUpdates v-else />

                        <template v-if="!recallOnlyJourney">
                            <template v-if="fallbackPricing">
                                <p class="card__text">We are unable to return pricing right now for your service. Please select one of the options below and a member of our customer hub will be in contact to discuss pricing with you.</p>
                            </template>
                            <template v-else>
                                <div class="service-options__blue-box" v-if="haveServicePlan">This vehicle has a service plan against it. A customer advisor will confirm pricing on booking.</div>
                                <h3 class="card__heading service-options__card-heading">
                                    <span v-if="showExclusiveServiceOffer">Exclusive service offer</span>
                                    <span v-else>Scheduled service</span>
                                </h3>
                                <p class="card__text">
                                    <span v-if="!showExclusiveServiceOffer"> Your vehicle is due a {{ scheduledService.display_name }}.</span>
                                    Our manufacturer-trained technicians will be using <span class="capitalize">{{ computedBrand }}</span>
                                    {{ approvedOrGenuine }} parts to carry out this work. Each service at Stellantis &You benefits from a complimentary full vehicle health check and wash &amp; vac so you can drive away with full peace of mind.
                                </p>
                            </template>

                            <div class="service-options__radio-section" v-if="scheduledService">
                                <label for="oneOff" class="service-options__radio-container">
                                    <div class="service-options__radio-label" :class="{ 'service-options__radio-label--row': haveServicePlan }">
                                        <div class="service-options__label-left">
                                            <span class="service-options__label-text">{{ scheduledService.display_name }}</span>
                                        </div>

                                        <div class="service-options__label-right" :class="{ 'service-options__label-right--flex-end-content': haveServicePlan }">
                                            <span class="service-options__price-with-switch" v-if="!haveServicePlan && scheduledService.price > 0">{{ scheduledService.display_price }}</span>
                                            <label for="oneOff" class="service-options__switch">
                                                <input type="checkbox" id="oneOff" name="service_plan" v-model="selectedOptions.service" class="service-options__switch-input" />
                                                <span class="service-options__slider"></span>
                                                <span class="service-options__yes">Yes</span>
                                                <span class="service-options__no">No</span>
                                            </label>
                                        </div>
                                    </div>
                                </label>
                            </div>

                            <template v-if="shouldShowMot">
                                <h3 class="card__heading service-options__card-heading" style="margin-top: 20px">MOT</h3>
                                <p class="card__text" style="margin-top: 15px">
                                    <template v-if="motExpiryKnown === true">Your MOT Test is due in the next {{ nextMotDueInDays }} days. </template>Our manufacturer-trained, DVSA accredited
                                    <span class="capitalize">{{ computedBrand }}</span> technicians will complete the MOT.
                                </p>

                                <div class="service-options__radio-section">
                                    <label for="mot" class="service-options__radio-container">
                                        <div class="service-options__radio-label">
                                            <div class="service-options__label-left">
                                                <span class="service-options__label-text">MOT</span>
                                            </div>

                                            <div class="service-options__label-right">
                                                <span v-if="!haveServicePlan && haveMotPricing" class="service-options__price-with-switch">{{ pricingData.mot_pricing.display_price }}</span>
                                                <label for="mot" class="service-options__switch">
                                                    <input type="checkbox" id="mot" name="mot" value="mot" v-model="selectedOptions.mot" class="service-options__switch-input" />
                                                    <span class="service-options__slider"></span>
                                                    <span class="service-options__yes">Yes</span>
                                                    <span class="service-options__no">No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </template>

                            <template v-if="shouldShowSystemDiagnostic">
                                <h3 class="card__heading service-options__card-heading" style="margin-top: 20px">Diagnostic Investigation</h3>
                                <p class="card__text" style="margin-top: 15px">
                                    Our manufacturer trained, Stellantis accredited <span class="capitalize">{{ computedBrand }}</span> technicians will carry out an initial investigation into the fault on your vehicle. Further diagnostic work may be needed to fully identify and resolve the fault. The fee shown covers the initial investigation only, and the final cost may vary depending on the time required. Please note that we will not carry out any work without your prior approval.
                                </p>

                                <div class="service-options__radio-section">
                                    <label for="system_diagnostic" class="service-options__radio-container">
                                        <div class="service-options__radio-label">
                                            <div class="service-options__label-left">
                                                <span class="service-options__label-text">Diagnostic Investigation</span>
                                            </div>

                                            <div class="service-options__label-right">
                                                <span v-if="!haveServicePlan && haveSystemDiagnosticPricing" class="service-options__price-with-switch">{{ systemDiagnosticPricing.display_price }}</span>
                                                <label for="system_diagnostic" class="service-options__switch">
                                                    <input type="checkbox" id="system_diagnostic" name="system_diagnostic" value="system_diagnostic" v-model="selectedOptions.system_diagnostic" class="service-options__switch-input" />
                                                    <span class="service-options__slider"></span>
                                                    <span class="service-options__yes">Yes</span>
                                                    <span class="service-options__no">No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </label>
                                </div>

                                <template v-if="selectedOptions.system_diagnostic">
                                    <h3 id="fault_description_heading" class="card__heading service-options__card-heading" style="margin-top: 20px">Please describe the fault you're experiencing</h3>
                                    <p class="card__text">Please tell us what's happening, such as any warning lights, noises or when the problem occurs.</p>

                                    <div class="service-options__radio-section">
                                        <textarea id="fault_description" name="fault_description" maxlength="1000" required aria-labelledby="fault_description_heading" v-model="faultDescriptionInput" class="service-options__fault-description"></textarea>
                                    </div>
                                </template>
                            </template>

                            <template v-if="pricingData.winter_check_pricing">
                                <h3 class="card__heading service-options__card-heading" style="margin-top: 20px">Winter Check</h3>
                                <p class="card__text">
                                    Receive a 29 point Winter Safety Check from our manufacturer-trained technicians so your vehicle can run smoothly all winter. We will check your tyres, wheels, battery, fluids, wiper blades, lights and top up your vehicles 6 vital fluids.
                                </p>

                                <div class="service-options__radio-section">
                                    <label for="winter_check" class="service-options__radio-container">
                                        <div class="service-options__radio-label">
                                            <div class="service-options__label-left">
                                                <span class="service-options__label-text">Winter Check</span>
                                            </div>

                                            <div class="service-options__label-right">
                                                <span class="service-options__price-with-switch" v-if="!haveServicePlan && pricingData.winter_check_pricing.display_price">{{ pricingData.winter_check_pricing.display_price }}</span>
                                                <label for="winter_check" class="service-options__switch">
                                                    <input type="checkbox" id="winter_check" name="winter_check" value="courtesy_car" v-model="selectedOptions.winter_check" class="service-options__switch-input" />
                                                    <span class="service-options__slider"></span>
                                                    <span class="service-options__yes">Yes</span>
                                                    <span class="service-options__no">No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </template>

                            <template v-if="pricingData.aircon_refresh_pricing">
                                <h3 class="card__heading service-options__card-heading" style="margin-top: 20px">Air Conditioning Refresh<span v-if="pricingData.aircon_refresh_pricing.show_aircon_refresh_own_vehicles"> and Full Anti-Bacterial Clean</span></h3>
                                <p class="card__text">
                                    Keep your vehicle cool, comfortable and fresh with our Air Conditioning Refresh. We will check all the main elements of your Air conditioning, including heater controls, internal fan speeds and drive belt.
                                    <span v-if="pricingData.aircon_refresh_pricing.show_aircon_refresh_own_vehicles">Plus we will give your Air conditioning a powerful deep clean to remove bacteria and leave your vehicle smelling fresh.</span>
                                </p>

                                <div class="service-options__radio-section">
                                    <label for="aircon_refresh" class="service-options__radio-container">
                                        <div class="service-options__radio-label">
                                            <div class="service-options__label-left">
                                                <span class="service-options__label-text">Air Conditioning Refresh<span v-if="pricingData.aircon_refresh_pricing.show_aircon_refresh_own_vehicles"> &amp; Anti-Bacterial Clean</span></span>
                                            </div>

                                            <div class="service-options__label-right">
                                                <span class="service-options__price-with-switch" v-if="!haveServicePlan && pricingData.aircon_refresh_pricing.display_price">{{ pricingData.aircon_refresh_pricing.display_price }}</span>
                                                <label for="aircon_refresh" class="service-options__switch">
                                                    <input type="checkbox" id="aircon_refresh" name="aircon_refresh" value="courtesy_car" v-model="selectedOptions.aircon_refresh" class="service-options__switch-input" />
                                                    <span class="service-options__slider"></span>
                                                    <span class="service-options__yes">Yes</span>
                                                    <span class="service-options__no">No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </template>

                            <AdditionalServicesOptions v-if="haveAdditionalServices" style="margin-top: 20px;" :additonal-services="filteredAdditionalServices" :selected-options="selectedOptions" @open-modal="setModal" />
                        </template>
                    </div>
                </div>

                <BookingOverview />

                <p v-if="validationMessage" class="service-options__validation-error">{{ validationMessage }}</p>

                <button v-if="!showNoOutstandingRecalls" type="button" class="btn btn--primary btn--full-width card__button service-options__button" @click.prevent="next_step(false)">
                    <span class="find-my-car__used-btn-txt">Continue</span>
                </button>
            </div>

            <AdditionalServicesModal v-if="modalOpen" :modal-open="modalOpen" :modal-data="modalData" @close-modal="closeModal" />
            <Modal :open="waitingForManufacturerUpdateModalOpen" :vertical-content="true" :is-card="true" :show-close-icon="true" :is-dismissed-on-click-backdrop="true" @close="waitingForManufacturerUpdateModalOpen = false">
                <template v-slot:header>
                    <div class="modal__header-container">
                        <h2 class="modal__header-title">Waiting for manufacturer update</h2>
                    </div>
                </template>
                <template v-slot:body>
                    <p class="modal__body-text">We are currently checking for any manufacturer updates on your vehicle. This may take a few moments.</p>
                    <p class="modal__body-text">If you wish to continue without checking for updates, please click continue below.</p>
                </template>
                <template v-slot:buttons>
                    <button class="btn btn--primary" @click="next_step(true)">Continue</button>
                </template>
            </Modal>
        </div>
    </section>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import { BookingOverview, LoadingSpinner, StepBar, StepLink } from "../index.js";
import EventObject from "../../../modules/tracking/EventObject";
import AdditionalServicesOptions from "./AdditionalServicesOptions.vue";
import AdditionalServicesModal from "./AdditionalServicesModal.vue";
import Modal from './../../../core/components/Modal.vue';
import ManufacturerUpdates from './ManufacturerUpdates.vue';
import numberFormat from "../../../core/modules/number-format.js";

export default {
    components: {
        AdditionalServicesOptions,
        AdditionalServicesModal,
        BookingOverview,
        LoadingSpinner,
        StepBar,
        StepLink,
        Modal,
        ManufacturerUpdates
    },

    watch: {
        'selectedOptions.black_friday_service_and_mot'(newVal) {
            if (newVal === true) {
                // When Black Friday bundle is selected, clear service and mot
                this.selectedOptions.service = false;
                this.selectedOptions.mot = false;
            }
        },

        'selectedOptions.service'(newVal) {
            if (newVal === true) {
                // Selecting a service should unselect the Black Friday bundle
                this.selectedOptions.black_friday_service_and_mot = false;
            }
        },

        'selectedOptions.mot'(newVal) {
            if (newVal === true) {
                // Selecting MOT should unselect the Black Friday bundle
                this.selectedOptions.black_friday_service_and_mot = false;
            }
        }
    },

    data() {
        return {
            modalData: null,
            modalOpen: false,
            waitingForManufacturerUpdateModalOpen: false,
            validationMessage: false,
        };
    },

    computed: {
        ...mapState("OSB", [
            "recallOnlyJourney",
            "pricingData",
            "selectedOptions",
            "motDue",
            "motExpiryKnown",
            "selectedVehicle",
            "otherSelected",
            "nonFranchiseVehicle",
            "carNotFound",
            "brand",
            "waitingForPricing",
            "waitingForServicePlan",
            "fallbackPricing",
            'haveServicePlan',
            "waitingForManufacturerUpdate",
            "manufacturerUpdate",
            "step",
            "shortOnlineServiceBooking",
            "manufacturerUpdatesSelected",
            "faultDescription",
        ]),
        ...mapState("Dealers", ["selectedDealer"]),

        showNoOutstandingRecalls() {
            return this.recallOnlyJourney
                && !this.waitingForManufacturerUpdate
                && this.manufacturerUpdate
                && Object.prototype.hasOwnProperty.call(this.manufacturerUpdate, 'records')
                && (!this.haveManufactureUpdate || Object.keys(this.manufacturerUpdate.records || {}).length === 0);
        },

        nextMotDueInDays() {
            if (!this.shouldShowMot) {
                return '';
            }
            if (this.selectedVehicle === null || typeof this.selectedVehicle.days_till_next_mot === 'undefined') {
                return '';
            }
            return numberFormat(this.selectedVehicle.days_till_next_mot, 0);
        },

        haveAdditionalServices() {
            return this.filteredAdditionalServices.length > 0;
        },

        filteredAdditionalServices() {
            if (this.pricingData === null || typeof this.pricingData.additional_services === 'undefined') {
                return [];
            }
            return this.pricingData.additional_services.filter((item) => item.code !== "CCACRP");
        },

        finishedCollectingPricing() {
            if (
                this.waitingForPricing == false
                && this.waitingForServicePlan == false
                // && this.waitingForManufacturerUpdate == false @TODO: Re-enable with apify
            ) {
                return true;
            }
            return false;
        },

        approvedOrGenuine() {
            return this.pricingData.service_pricing.age > 2 ? "approved" : "genuine";
        },

        computedBrand() {
            const brand = this.otherSelected ? "" : this.brand;
            if (brand == 'citroen') {
                return 'Citroën';
            }
            if (brand == 'ds') {
                return 'DS';
            }
            return brand;
        },

        showExclusiveServiceOffer() {
            if (this.scheduledService && this.scheduledService.new_customer_offer === true) {
                return true;
            }
            return false;
        },

        scheduledService() {
            if (typeof this.pricingData.service_pricing == 'undefined') {
                return false;
            }

            return this.pricingData.service_pricing;
        },

        shouldShowMot() {
            if (!this.motPricing) {
                return false;
            }
            if (this.motDue == false) {
                return false;
            }
            return true;
        },

        motPricing() {
            if (typeof this.pricingData.mot_pricing == 'undefined') {
                return false;
            }

            return this.pricingData.mot_pricing;
        },

        courtesyCarPricing() {
            if (typeof this.pricingData.courtesy_car_pricing == 'undefined') {
                return false;
            }

            return this.pricingData.courtesy_car_pricing;
        },

        systemDiagnosticPricing() {
            if (typeof this.pricingData.system_diagnostic_pricing == 'undefined') {
                return false;
            }

            return this.pricingData.system_diagnostic_pricing;
        },

        haveMotPricing() {
            if (this.motPricing.price > 0) {
                return true;
            }
            return false;
        },

        haveSystemDiagnosticPricing() {
            if (this.systemDiagnosticPricing && this.systemDiagnosticPricing.price > 0) {
                return true;
            }
            return false;
        },

        shouldShowSystemDiagnostic() {
            return this.haveSystemDiagnosticPricing;
        },

        faultDescriptionInput: {
            get() {
                return this.faultDescription;
            },
            set(value) {
                this.setFaultDescription(value);
            },
        },

        haveManufactureUpdate() {
            if (this.manufacturerUpdate?.['needsCheck'] == true) {
                return true;
            }
            return false;
        },
    },

    methods: {
        ...mapActions("OSB", ["startStandardBooking"]),
        ...mapMutations("OSB", ["incrementStep", "setStep", "setFaultDescription"]),

        bookServiceOrMot() {
            this.validationMessage = false;
            this.startStandardBooking();
            this.scrollToTop();
        },

        scrollToTop() {
            window.scrollTo(0, 0);
        },

        next_step(skipWaitingForManufacturerUpdate = false) {
            if (
                this.haveManufactureUpdate == false &&
                this.selectedOptions.service == false &&
                this.selectedOptions.mot == false &&
                this.selectedOptions.system_diagnostic == false &&
                this.selectedOptions.aircon_refresh == false &&
                this.selectedOptions.pollen_filter == false &&
                this.selectedOptions.black_friday_service_and_mot == false
            ) {
                this.validationMessage = "Please select an option to proceed";
                return;
            }

            if (this.selectedOptions.system_diagnostic && this.faultDescription.trim() === "") {
                this.validationMessage = "Please describe the fault you're experiencing";
                return;
            }

            if (!skipWaitingForManufacturerUpdate && this.waitingForManufacturerUpdate) {
                this.waitingForManufacturerUpdateModalOpen = true;
                return;
            }

            console.log('Proceeding to next step');
            new EventObject("OSB", "Click", "Service options continue clicked").dispatch();

            this.incrementStep();
            this.scrollToTop();
        },

        setModal(item) {
            this.modalData = item;
            this.modalOpen = true;
        },

        closeModal() {
            this.modalOpen = false;
            this.modalData = null;
        },
        // Manufacturer update toggle logic moved to ManufacturerUpdates component
    },
};
</script>

<style lang="scss" scoped></style>
